/* FactoringPanel — IBAN Factoring instellingen (cessie van factuur).
   Toon getoond binnen Instellingen → Bedrijf, als uitklapbare sectie.

   Props:
     form        — huidige instellingen-form state (object)
     setForm     — setter voor form (function)
     IOSToggle   — toggle-component prop (re-use van parent)
*/
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import API from '@/lib/api';
import { Eye, ArrowCounterClockwise, FileText, UploadSimple, Trash, FilePdf, Translate } from '@phosphor-icons/react';

const TAAL_LABELS = { nl: 'Nederlands', fr: 'Frans', en: 'Engels', de: 'Duits' };
const TAAL_KEYS = ['nl', 'fr', 'en', 'de'];

export default function FactoringPanel({ form, setForm, IOSToggle }) {
  const u = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target?.value ?? e }));
  const [taal, setTaal] = useState('nl');
  const [previewing, setPreviewing] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [translating, setTranslating] = useState(false);
  // Voorwaarden-bestanden (geüpload via API)
  const [voorwaarden, setVoorwaarden] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadVoorwaarden = async () => {
    try {
      const r = await API.get('/instellingen/factoring/voorwaarden');
      setVoorwaarden(r.data?.items || []);
    } catch (e) {
      // stilletjes; user kan ook leeg starten
    }
  };
  useEffect(() => { loadVoorwaarden(); }, []);

  const handleUploadVoorwaarden = async (file, lang = 'all') => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Alleen PDF-bestanden toegestaan');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Bestand te groot (max 8MB)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('lang', lang);
      fd.append('file', file);
      await API.post('/instellingen/factoring/upload-voorwaarden', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Algemene Voorwaarden geüpload (${lang})`);
      await loadVoorwaarden();
    } catch (e) {
      toast.error('Upload mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteVoorwaarden = async (lang) => {
    if (!window.confirm(`Algemene Voorwaarden voor "${lang === 'all' ? 'Alle talen' : TAAL_LABELS[lang] || lang}" verwijderen?`)) return;
    try {
      await API.delete(`/instellingen/factoring/voorwaarden?lang=${encodeURIComponent(lang)}`);
      toast.success('Verwijderd');
      await loadVoorwaarden();
    } catch (e) {
      toast.error('Verwijderen mislukt');
    }
  };

  const handlePreviewVoorwaarden = async (lang) => {
    try {
      const res = await API.get(`/instellingen/factoring/voorwaarden/download?lang=${encodeURIComponent(lang)}`,
        { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Preview mislukt');
    }
  };

  const handleTranslateFromCurrent = async () => {
    const srcTekst = (form[`factoring_tekst_${taal}`] || '').trim();
    const srcBrief = (form[`factoring_introbrief_${taal}`] || '').trim();
    if (!srcTekst && !srcBrief) {
      toast.error(`Vul eerst de teksten in het ${TAAL_LABELS[taal]} tabblad in`);
      return;
    }
    const targets = TAAL_KEYS.filter((t) => t !== taal);
    const targetLabels = targets.map((t) => TAAL_LABELS[t]).join(', ');
    if (!window.confirm(
      `Vertaal van ${TAAL_LABELS[taal]} naar ${targetLabels}?\n\n` +
      `⚠ Bestaande teksten in deze talen worden OVERSCHREVEN.\n\n` +
      `(De vertalingen worden direct opgeslagen — je kan ze daarna handmatig fijn-slijpen per tab.)`
    )) return;

    setTranslating(true);
    try {
      // Eerst huidige source-tekst opslaan (zodat AI de allerlaatste versie gebruikt)
      const saveData = {};
      if (srcTekst) saveData[`factoring_tekst_${taal}`] = srcTekst;
      if (srcBrief) saveData[`factoring_introbrief_${taal}`] = srcBrief;
      await API.put('/instellingen', saveData);
      // Roep translate aan
      const r = await API.post('/instellingen/factoring/translate', {
        source_lang: taal,
        targets,
      });
      const trans = r.data?.translations || {};
      // Update form-state met vertaalde versies
      setForm((p) => {
        const next = { ...p };
        for (const tgt of targets) {
          if (trans[`tekst_${tgt}`]) next[`factoring_tekst_${tgt}`] = trans[`tekst_${tgt}`];
          if (trans[`introbrief_${tgt}`]) next[`factoring_introbrief_${tgt}`] = trans[`introbrief_${tgt}`];
        }
        return next;
      });
      const okLangs = targets.filter((t) => trans[`tekst_${t}`] || trans[`introbrief_${t}`]);
      if (okLangs.length === 0) {
        toast.error('Vertaling mislukt — probeer opnieuw');
      } else {
        toast.success(`Vertaald naar: ${okLangs.map((t) => TAAL_LABELS[t]).join(', ')}`);
      }
    } catch (e) {
      toast.error('Vertaling mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setTranslating(false);
    }
  };

  const loadDefaults = async () => {
    if (defaults) return defaults;
    try {
      const r = await API.get('/instellingen/factoring/defaults');
      setDefaults(r.data?.defaults || {});
      return r.data?.defaults || {};
    } catch (e) {
      toast.error('Kon defaults niet ophalen');
      return {};
    }
  };

  const resetTekstNaarDefault = async () => {
    const d = await loadDefaults();
    const key = `tekst_${taal}`;
    const v = d?.[key] || '';
    if (!v) {
      toast.error('Geen default beschikbaar voor deze taal');
      return;
    }
    setForm((p) => ({ ...p, [`factoring_tekst_${taal}`]: v }));
    toast.success(`Tekst (${TAAL_LABELS[taal]}) hersteld naar BNP Paribas standaard`);
  };

  const resetBriefNaarDefault = async () => {
    const d = await loadDefaults();
    const key = `introbrief_${taal}`;
    const v = d?.[key] || '';
    if (!v) {
      toast.error('Geen default beschikbaar voor deze taal');
      return;
    }
    setForm((p) => ({ ...p, [`factoring_introbrief_${taal}`]: v }));
    toast.success(`Introductiebrief (${TAAL_LABELS[taal]}) hersteld naar BNP Paribas standaard`);
  };

  const previewIntrobrief = async () => {
    if (previewing) return;
    setPreviewing(true);
    try {
      // Eerst huidige instellingen opslaan zodat de preview de nieuwste templates gebruikt
      const saveData = {};
      TAAL_KEYS.forEach((t) => {
        if (form[`factoring_tekst_${t}`] !== undefined) saveData[`factoring_tekst_${t}`] = form[`factoring_tekst_${t}`];
        if (form[`factoring_introbrief_${t}`] !== undefined) saveData[`factoring_introbrief_${t}`] = form[`factoring_introbrief_${t}`];
      });
      saveData.factoring_actief = !!form.factoring_actief;
      saveData.factoring_email = form.factoring_email || '';
      saveData.factoring_ondertek_voornaam = form.factoring_ondertek_voornaam || '';
      saveData.factoring_ondertek_naam = form.factoring_ondertek_naam || '';
      saveData.factoring_ondertek_functie = form.factoring_ondertek_functie || '';
      await API.put('/instellingen', saveData);

      // Preview-PDF ophalen
      const res = await API.post(
        '/instellingen/factoring/preview-introbrief',
        { lang: taal },
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Voorbeeld genereren mislukt: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setPreviewing(false);
    }
  };

  const actief = !!form.factoring_actief;
  const heeftIban2 = !!(form.iban_2 || '').trim();

  return (
    <div className="p-5 space-y-4" data-testid="factoring-panel">
      {/* Master toggle */}
      <div className="flex items-start gap-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
        <IOSToggle
          checked={actief}
          onChange={(v) => setForm((p) => ({ ...p, factoring_actief: v }))}
          size="sm"
          showLabel={false}
        />
        <div className="flex-1">
          <div className="text-sm font-semibold text-orange-900" data-testid="factoring-actief-label">
            IBAN Factoring activeren
          </div>
          <div className="text-[11px] text-orange-700 leading-relaxed mt-0.5">
            Wanneer een klant <strong>bank-keuze = IBAN 2</strong> heeft, wordt op de factuur automatisch:
            <br/>• Een cessie-tekst toegevoegd in de taal van de klant (NL/FR/EN/DE)
            <br/>• Een <strong>introductiebrief-PDF</strong> als bijlage meegestuurd
            <br/>• <strong>Algemene Voorwaarden</strong> als bijlage (indien geüpload)
            <br/>• Een kopie naar het factoring-emailadres (CC) verzonden
          </div>
        </div>
      </div>

      {!heeftIban2 && actief && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-[12px] text-amber-800">
          ⚠ <strong>Let op:</strong> IBAN 2 is nog niet ingesteld. Vul deze hierboven in (sectie
          &quot;Algemeen&quot;) — anders heeft factoring geen effect.
        </div>
      )}

      {actief && (
        <>
          {/* Factoring email (CC) */}
          <div>
            <label className="form-label">
              E-mailadres factoringmaatschappij (CC bij verzending)
            </label>
            <input
              type="email"
              className="field-input text-sm"
              value={form.factoring_email || ''}
              onChange={u('factoring_email')}
              placeholder="bv. info@bnpparibasfortisfactor.com"
              data-testid="factoring-email"
            />
            <div className="text-[10px] text-gray-500 mt-1">
              Als ingevuld → ontvangt de factor automatisch een kopie van elke verstuurde factuur (CC) met
              alle bijlagen (factuur + introductiebrief).
            </div>
          </div>

          {/* Ondertekenaar override */}
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
            <div className="text-xs font-semibold text-gray-700 mb-1">Ondertekenaar (introductiebrief)</div>
            <div className="text-[10px] text-gray-500 mb-2">
              Laat leeg om de naam van de ingelogde gebruiker te gebruiken.
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="form-label">Voornaam</label>
                <input
                  className="field-input text-xs"
                  value={form.factoring_ondertek_voornaam || ''}
                  onChange={u('factoring_ondertek_voornaam')}
                  placeholder="Jan"
                  data-testid="factoring-onderteken-voornaam"
                />
              </div>
              <div>
                <label className="form-label">Naam</label>
                <input
                  className="field-input text-xs"
                  value={form.factoring_ondertek_naam || ''}
                  onChange={u('factoring_ondertek_naam')}
                  placeholder="Janssens"
                  data-testid="factoring-onderteken-naam"
                />
              </div>
              <div>
                <label className="form-label">Functie</label>
                <input
                  className="field-input text-xs"
                  value={form.factoring_ondertek_functie || ''}
                  onChange={u('factoring_ondertek_functie')}
                  placeholder="Zaakvoerder"
                  data-testid="factoring-onderteken-functie"
                />
              </div>
            </div>
          </div>

          {/* Algemene Voorwaarden PDF upload */}
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-xs font-semibold text-gray-700">📎 Algemene Voorwaarden Factoring (PDF)</div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  Optioneel — wordt automatisch als bijlage meegestuurd bij elke factuur naar factoring-klanten.
                  Eén bestand voor alle talen, of upload een aparte versie per taal.
                </div>
              </div>
            </div>

            {/* Lijst van geüploade bestanden */}
            {voorwaarden.length > 0 && (
              <div className="space-y-1.5 mb-3" data-testid="factoring-voorwaarden-lijst">
                {voorwaarden.map((v) => (
                  <div
                    key={v.file_id}
                    className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded text-xs"
                    data-testid={`voorwaarden-row-${v.lang}`}
                  >
                    <FilePdf size={16} className="text-red-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-gray-800 truncate">
                        Algemene_Voorwaarden_Factoring{v.lang === 'all' ? '' : `_${v.lang.toUpperCase()}`}.pdf
                      </div>
                      <div className="text-[10px] text-gray-500">
                        {v.lang === 'all' ? '🌐 Alle talen' : `🌍 ${TAAL_LABELS[v.lang] || v.lang}`}
                        {' · '}
                        {(v.size / 1024).toFixed(1)} KB
                        {' · '}
                        Origineel: <span className="font-mono">{v.filename}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePreviewVoorwaarden(v.lang)}
                      className="text-blue-600 hover:text-blue-800 p-1"
                      title="Voorbeeld"
                      data-testid={`voorwaarden-preview-${v.lang}`}
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteVoorwaarden(v.lang)}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Verwijder"
                      data-testid={`voorwaarden-delete-${v.lang}`}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload control */}
            <div className="flex items-center gap-2">
              <select
                id="voorw-lang-select"
                className="field-input text-xs"
                style={{ maxWidth: 160 }}
                defaultValue="all"
                data-testid="factoring-voorw-lang-select"
              >
                <option value="all">🌐 Alle talen</option>
                <option value="nl">🇳🇱 Nederlands</option>
                <option value="fr">🇫🇷 Frans</option>
                <option value="en">🇬🇧 Engels</option>
                <option value="de">🇩🇪 Duits</option>
              </select>
              <input
                type="file"
                ref={fileInputRef}
                accept="application/pdf,.pdf"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const langSel = document.getElementById('voorw-lang-select');
                  const lang = langSel ? langSel.value : 'all';
                  handleUploadVoorwaarden(f, lang);
                }}
                className="hidden"
                data-testid="factoring-voorw-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="text-xs px-3 py-2 bg-orange-600 text-white hover:bg-orange-700 rounded font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="factoring-voorw-upload-btn"
              >
                <UploadSimple size={14} />
                {uploading ? 'Bezig...' : 'PDF uploaden'}
              </button>
            </div>
          </div>

          {/* Taal-tabs + Vertaal-knop */}
          <div className="border-b border-gray-200 flex items-center gap-1">
            {TAAL_KEYS.map((t) => (
              <button
                key={t}
                onClick={() => setTaal(t)}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  taal === t
                    ? 'border-orange-600 text-orange-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`factoring-taal-tab-${t}`}
              >
                {TAAL_LABELS[t]}
              </button>
            ))}
            <button
              type="button"
              onClick={handleTranslateFromCurrent}
              disabled={translating}
              className="ml-auto mb-[-1px] px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-t-md inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors"
              title={`Vertaal van ${TAAL_LABELS[taal]} naar de overige 3 talen via AI`}
              data-testid="factoring-translate-btn"
            >
              <Translate size={13} weight="bold" />
              {translating ? 'Bezig...' : `Vertaal vanuit ${TAAL_LABELS[taal]}`}
            </button>
          </div>

          {/* Korte tekst op factuur */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="form-label">
                Cessie-tekst op factuur — {TAAL_LABELS[taal]}
              </label>
              <button
                type="button"
                onClick={resetTekstNaarDefault}
                className="text-[11px] text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1"
                data-testid={`factoring-reset-tekst-${taal}`}
              >
                <ArrowCounterClockwise size={11} /> Standaard (BNP)
              </button>
            </div>
            <textarea
              className="field-input text-xs font-mono leading-relaxed"
              rows={6}
              value={form[`factoring_tekst_${taal}`] || ''}
              onChange={u(`factoring_tekst_${taal}`)}
              placeholder="Deze factuur werd gecedeerd aan..."
              data-testid={`factoring-tekst-${taal}`}
            />
            <div className="text-[10px] text-gray-400 mt-1">
              Variabelen: <code>{'{factor_iban}'}</code>, <code>{'{factor_bic}'}</code>,{' '}
              <code>{'{klant_naam}'}</code>, <code>{'{factuurnummer}'}</code>,{' '}
              <code>{'{bedrijf_naam}'}</code>, <code>{'{datum}'}</code>
            </div>
          </div>

          {/* Lange introductiebrief */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="form-label">
                Introductiebrief (volledige tekst) — {TAAL_LABELS[taal]}
              </label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={resetBriefNaarDefault}
                  className="text-[11px] text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1"
                  data-testid={`factoring-reset-brief-${taal}`}
                >
                  <ArrowCounterClockwise size={11} /> Standaard (BNP)
                </button>
                <button
                  type="button"
                  onClick={previewIntrobrief}
                  disabled={previewing}
                  className="text-[11px] text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 disabled:opacity-50"
                  data-testid={`factoring-preview-${taal}`}
                >
                  <Eye size={11} /> {previewing ? 'Bezig...' : 'Voorbeeld PDF'}
                </button>
              </div>
            </div>
            <textarea
              className="field-input text-xs font-mono leading-relaxed"
              rows={14}
              value={form[`factoring_introbrief_${taal}`] || ''}
              onChange={u(`factoring_introbrief_${taal}`)}
              placeholder="Geachte mevrouw, meneer,&#10;&#10;Om ons meer te kunnen concentreren op..."
              data-testid={`factoring-introbrief-${taal}`}
            />
            <div className="text-[10px] text-gray-400 mt-1 flex items-start gap-1">
              <FileText size={11} className="mt-0.5" />
              <span>
                Beschikbare variabelen: <code>{'{klant_naam}'}</code>,{' '}
                <code>{'{contactpersoon}'}</code>, <code>{'{klant_adres}'}</code>,{' '}
                <code>{'{datum}'}</code>, <code>{'{bedrijf_naam}'}</code>,{' '}
                <code>{'{factor_iban}'}</code>, <code>{'{factor_bic}'}</code>,{' '}
                <code>{'{ondertekenaar_voornaam}'}</code>,{' '}
                <code>{'{ondertekenaar_naam}'}</code>, <code>{'{ondertekenaar_functie}'}</code>
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
